import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import EmailDeliverySection from "./email_delivery_section";
import SelectedConsultSlots from "./selected_consult_slots";
import ClassicConsultAvailability from "./classic_consult_availability";
import ConsultAvailabilityCalendar from "./consult_availability_calendar";
import { officeToHomeConflictMessage } from "./office_to_home_warning";
import { selectedSlotFromEntry, type ConsultAvailability, type ConsultAvailabilityDate, type SelectedConsultSlot } from "./consult_availability";

import {
  AlertBanner,
  EmailDetailsSection,
  ErrorList,
  ExistingAppointmentsNotice,
  type BaseInviteFormState,
  type Option,
  type PaymentClassificationPayload,
  type WorkspaceBase,
  openSchedulingPreview,
  responseErrorMessage,
} from "./shared";

interface ConsultInviteFormState extends BaseInviteFormState {
  scheduledSendAt: string;
  paymentClassification: string;
  slotTypeFilter: string;
}

interface ConsultSchedulingInviteWorkspacePayload extends WorkspaceBase<ConsultInviteFormState> {
  customSlotUrl: string;
  payment: PaymentClassificationPayload;
  availability: ConsultAvailability;
}

interface WorkspaceResponse {
  workspace?: ConsultSchedulingInviteWorkspacePayload;
  errors?: string[];
  redirectPath?: string;
  notice?: string;
}

interface CustomSlotDraft {
  date: string;
  time: string;
  devaLocation: string;
  offeredVisitTypes: string[];
}

interface CustomSlotResponse {
  slot?: SelectedConsultSlot;
  errors?: string[];
}

const SLOT_TYPE_OPTIONS: Option[] = [
  { label: "All locations", value: "all" },
  { label: "Deva in office", value: "office" },
  { label: "Deva at home", value: "home" },
];

const DEVA_LOCATION_OPTIONS: Option[] = [
  { label: "Deva in office", value: "office" },
  { label: "Deva at home", value: "home" },
];

const VISIT_TYPE_OPTIONS: Option[] = [
  { label: "In-person", value: "in_person" },
  { label: "Telehealth", value: "telehealth" },
  { label: "Video call", value: "video_call" },
  { label: "Phone call", value: "phone_call" },
];

function devaLocationLabel(value: string) {
  return DEVA_LOCATION_OPTIONS.find((option) => option.value === value)?.label || value;
}

function ChevronIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-4 w-4 transition group-open:rotate-180" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="m5 7.5 5 5 5-5" />
    </svg>
  );
}

function CustomSlotModal({
  defaultDate,
  defaultTime,
  defaultVisitTypes,
  saving,
  error,
  onClose,
  onSave,
}: {
  defaultDate: string;
  defaultTime: string;
  defaultVisitTypes: string[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (draft: CustomSlotDraft) => void;
}) {
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState(defaultTime);
  const [devaLocation, setDevaLocation] = useState("office");
  const [offeredVisitTypes, setOfferedVisitTypes] = useState(defaultVisitTypes);
  const [validationError, setValidationError] = useState<string | null>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const trigger = document.activeElement;
    dateInputRef.current?.focus();
    return () => {
      if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, saving]);

  const handleLocationChange = (value: string) => {
    setDevaLocation(value);
    if (value === "home") {
      setOfferedVisitTypes((current) => current.filter((visitType) => visitType !== "in_person"));
    }
  };

  const handleVisitTypeToggle = (visitType: string, checked: boolean) => {
    setOfferedVisitTypes((current) => (
      checked ? Array.from(new Set([...current, visitType])) : current.filter((value) => value !== visitType)
    ));
  };

  const handleSave = () => {
    if (!date || !time) {
      setValidationError("Choose a date and time.");
      return;
    }
    if (offeredVisitTypes.length === 0) {
      setValidationError("Choose at least one offered visit type.");
      return;
    }

    setValidationError(null);
    onSave({ date, time, devaLocation, offeredVisitTypes });
  };

  const modal = (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-neutral-950/55 px-4 py-6 print:hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="custom-consult-slot-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <div className="flex max-h-[calc(100vh-3rem)] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-neutral-200 px-5 py-4 dark:border-neutral-800">
          <div>
            <p className="app-kicker">Availability</p>
            <h3 id="custom-consult-slot-title" className="mt-1 text-lg font-semibold text-neutral-950 dark:text-white">Add custom consult time</h3>
          </div>
          <button type="button" className="app-button-ghost" disabled={saving} onClick={onClose} aria-label="Close custom consult time modal">
            Close
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto px-5 py-5">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Add a time outside the configured availability. It cannot overlap another scheduled consult.
          </p>

          {(validationError || error) ? (
            <div className="app-alert-error" role="alert">{validationError || error}</div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="custom-consult-date" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Date</label>
              <input
                ref={dateInputRef}
                id="custom-consult-date"
                type="date"
                className="app-input mt-1"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor="custom-consult-time" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Time</label>
              <input
                id="custom-consult-time"
                type="time"
                className="app-input mt-1"
                value={time}
                onChange={(event) => setTime(event.target.value)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="custom-consult-location" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Deva location</label>
            <select
              id="custom-consult-location"
              className="app-select mt-1"
              value={devaLocation}
              onChange={(event) => handleLocationChange(event.target.value)}
            >
              {DEVA_LOCATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <fieldset>
            <legend className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Offered visit types</legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {VISIT_TYPE_OPTIONS.map((option) => {
                const disabled = devaLocation === "home" && option.value === "in_person";
                return (
                  <label key={option.value} className={`app-check-card ${disabled ? "opacity-40" : ""}`}>
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={offeredVisitTypes.includes(option.value)}
                      onChange={(event) => handleVisitTypeToggle(option.value, event.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500 dark:border-neutral-700 dark:bg-neutral-900"
                    />
                    <span className="font-medium text-neutral-800 dark:text-neutral-100">{option.label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-neutral-200 px-5 py-4 dark:border-neutral-800">
          <button type="button" className="app-button-secondary" disabled={saving} onClick={onClose}>Cancel</button>
          <button type="button" className="app-button-primary" disabled={saving} onClick={handleSave}>
            {saving ? "Checking..." : "Add time"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

function ClassificationField({
  id,
  label,
  value,
  options,
  evidenceText,
  linkLabel,
  linkPath,
  emptyOption,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: Option[];
  evidenceText: string;
  linkLabel: string;
  linkPath: string;
  emptyOption?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex min-w-0 items-end gap-1.5">
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">
          {label}
        </label>
        <select id={id} className="app-select mt-1" required value={value} onChange={(event) => onChange(event.target.value)}>
          {emptyOption ? <option value="">{emptyOption}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <details className="group relative mb-0.5 shrink-0">
        <summary
          title={evidenceText}
          aria-label={`Show source for ${label}`}
          className="flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-md border border-neutral-200 text-xs font-bold text-neutral-500 marker:hidden hover:border-neutral-300 hover:text-neutral-700 [&::-webkit-details-marker]:hidden dark:border-neutral-800 dark:text-neutral-400 dark:hover:border-neutral-700 dark:hover:text-neutral-200"
        >
          i
        </summary>
        <div className="invisible absolute right-0 top-9 z-30 w-[min(20rem,calc(100vw-3rem))] rounded-lg border border-neutral-200 bg-white p-3 text-sm leading-5 text-neutral-600 opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100 group-open:visible group-open:opacity-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-300">
          <p>{evidenceText}</p>
          <a
            href={linkPath}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block font-semibold text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200"
          >
            {linkLabel}
          </a>
        </div>
      </details>
    </div>
  );
}

function defaultOfferedVisitTypes(devaLocation: string, patientStateClassification: string, paymentClassification: string) {
  const insuredArizonaPatient = patientStateClassification === "arizona_patient" && paymentClassification === "insurance";

  if (devaLocation === "office") {
    return ["in_person"];
  }

  return insuredArizonaPatient ? ["telehealth"] : ["video_call", "phone_call"];
}

function selectedKeysAfterToggle(selectedKeys: string[], key: string, checked: boolean) {
  const selectedSet = new Set(selectedKeys);

  if (checked) {
    selectedSet.add(key);
  } else {
    selectedSet.delete(key);
  }

  return Array.from(selectedSet);
}

function selectedSlotDetailsFromAvailability(availability: ConsultAvailability) {
  const details: Record<string, SelectedConsultSlot> = {};

  availability.dates.forEach((date) => {
    date.entries.forEach((entry) => {
      if (entry.selectable) details[entry.key] = selectedSlotFromEntry(date, entry);
    });
  });

  availability.selectedSlots.forEach((slot) => {
    details[slot.key] = slot;
  });

  return details;
}

function selectedSlotsFromKeys(selectedSlotKeys: string[], selectedSlotDetails: Record<string, SelectedConsultSlot>) {
  return selectedSlotKeys
    .map((slotKey) => selectedSlotDetails[slotKey])
    .filter((slot): slot is SelectedConsultSlot => Boolean(slot))
    .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time) || a.devaLocation.localeCompare(b.devaLocation));
}

export default function ConsultSchedulingInviteWorkspace(props: ConsultSchedulingInviteWorkspacePayload) {
  const [form, setForm] = useState<ConsultInviteFormState>(props.form);
  const [scheduleForLater, setScheduleForLater] = useState(Boolean(props.form.scheduledSendAt));
  const [availability, setAvailability] = useState<ConsultAvailability>(props.availability);
  const [selectedSlotKeys, setSelectedSlotKeys] = useState<string[]>(props.availability.selectedSlotKeys);
  const [selectedSlotDetails, setSelectedSlotDetails] = useState<Record<string, SelectedConsultSlot>>(() =>
    selectedSlotDetailsFromAvailability(props.availability),
  );
  const [customizedVisitTypeKeys, setCustomizedVisitTypeKeys] = useState<Set<string>>(() => new Set());
  const [defaultSelectionActive, setDefaultSelectionActive] = useState(props.availability.defaultSlotSelectionActive);
  const [errors, setErrors] = useState<string[]>(props.errors || []);
  const [flash, setFlash] = useState<{ tone: "error" | "success"; message: string } | null>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [jumpDate, setJumpDate] = useState(props.availability.startDate);
  const datePickerRef = useRef<HTMLDetailsElement>(null);
  const availabilityOptionsRef = useRef<HTMLDetailsElement>(null);
  const availabilityRequest = useRef<AbortController | null>(null);
  const selectionRevision = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const [customSlotDefaults, setCustomSlotDefaults] = useState<{ date: string; time: string } | null>(null);
  const [savingCustomSlot, setSavingCustomSlot] = useState(false);
  const [customSlotError, setCustomSlotError] = useState<string | null>(null);

  const openCustomSlot = (date: string, time: string) => {
    if (availabilityOptionsRef.current) availabilityOptionsRef.current.open = false;
    setCustomSlotError(null);
    setCustomSlotDefaults({ date, time });
  };

  const updateForm = <Key extends keyof ConsultInviteFormState>(field: Key, value: ConsultInviteFormState[Key]) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const applyWorkspace = (workspace: ConsultSchedulingInviteWorkspacePayload, source: "availability" | "submit", preserveSelection = false) => {
    const nextSelectedSlotDetails = selectedSlotDetailsFromAvailability(workspace.availability);

    setAvailability(workspace.availability);
    setJumpDate(workspace.availability.startDate);
    setErrors(workspace.errors || []);

    if (source === "submit") {
      setForm(workspace.form);
      setSelectedSlotKeys(workspace.availability.selectedSlotKeys);
      setSelectedSlotDetails(nextSelectedSlotDetails);
    } else {
      setForm((current) => ({ ...current, slotTypeFilter: workspace.form.slotTypeFilter }));
      if (workspace.availability.defaultSlotSelectionActive && !preserveSelection) {
        setSelectedSlotKeys(workspace.availability.selectedSlotKeys);
        setSelectedSlotDetails(nextSelectedSlotDetails);
      } else {
        setSelectedSlotDetails((current) => {
          const merged = { ...nextSelectedSlotDetails, ...current };
          Object.keys(nextSelectedSlotDetails).forEach((key) => {
            merged[key] = { ...merged[key], officeToHomeBuffer: nextSelectedSlotDetails[key].officeToHomeBuffer };
          });
          return merged;
        });
      }
    }

    setDefaultSelectionActive(!preserveSelection && workspace.availability.defaultSlotSelectionActive);
  };

  const loadAvailability = async (
    startDate: string,
    slotTypeFilter = form.slotTypeFilter,
    patientStateClassification = form.patientStateClassification,
    preserveSelection = false,
  ) => {
    availabilityRequest.current?.abort();
    const request = new AbortController();
    availabilityRequest.current = request;
    const revision = selectionRevision.current;
    setLoadingAvailability(true);
    setFlash(null);

    const url = new URL(props.availabilityUrl, window.location.origin);
    url.searchParams.set("consult_scheduling_invite[availability_start]", startDate);
    url.searchParams.set("consult_scheduling_invite[slot_type_filter]", slotTypeFilter);
    url.searchParams.set("consult_scheduling_invite[patient_state_classification]", patientStateClassification);
    url.searchParams.set("consult_scheduling_invite[payment_classification]", form.paymentClassification);

    if (!defaultSelectionActive || preserveSelection) {
      // Keep an explicitly empty selection distinct from the initial default selection.
      (selectedSlotKeys.length > 0 ? selectedSlotKeys : [""]).forEach((slotKey) => {
        url.searchParams.append("consult_scheduling_invite[selected_slot_keys][]", slotKey);
      });
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: request.signal,
      });
      const payload = (await response.json().catch(() => ({}))) as WorkspaceResponse;
      if (request.signal.aborted) return;

      if (!response.ok || !payload.workspace) {
        throw new Error(responseErrorMessage(payload, "Unable to load availability."));
      }

      applyWorkspace(payload.workspace, "availability", preserveSelection || revision !== selectionRevision.current);
    } catch (error) {
      if (request.signal.aborted) return;
      setForm((current) => ({ ...current, slotTypeFilter: availability.slotTypeFilter }));
      setFlash({ tone: "error", message: error instanceof Error ? error.message : "Unable to load availability." });
    } finally {
      if (!request.signal.aborted) setLoadingAvailability(false);
    }
  };

  useEffect(() => () => availabilityRequest.current?.abort(), []);

  const pageAvailability = (date: string) => {
    if (datePickerRef.current?.open) {
      datePickerRef.current.open = false;
      datePickerRef.current.querySelector("summary")?.focus();
    }
    void loadAvailability(date, form.slotTypeFilter, form.patientStateClassification, true);
  };

  const handleSlotTypeFilterChange = (value: string) => {
    updateForm("slotTypeFilter", value);
    void loadAvailability(availability.startDate, value);
  };

  const refreshDefaultVisitTypes = (patientStateClassification: string, paymentClassification: string) => {
    setSelectedSlotDetails((current) => {
      const next = { ...current };
      selectedSlotKeys.forEach((key) => {
        const slot = next[key];
        if (!slot || customizedVisitTypeKeys.has(key)) return;

        next[key] = {
          ...slot,
          offeredVisitTypes: defaultOfferedVisitTypes(slot.devaLocation, patientStateClassification, paymentClassification),
        };
      });
      return next;
    });
  };

  const handlePatientStateChange = (value: string) => {
    setForm((current) => ({
      ...current,
      patientStateClassification: value,
    }));
    refreshDefaultVisitTypes(value, form.paymentClassification);
    if (defaultSelectionActive) {
      void loadAvailability(availability.startDate, form.slotTypeFilter, value);
    }
  };

  const handlePaymentClassificationChange = (value: string) => {
    selectionRevision.current += 1;
    updateForm("paymentClassification", value);
    refreshDefaultVisitTypes(form.patientStateClassification, value);
  };

  const handleSlotToggle = (slot: SelectedConsultSlot, checked: boolean) => {
    selectionRevision.current += 1;
    setDefaultSelectionActive(false);
    setSelectedSlotDetails((current) => {
      if (checked) {
        return {
          ...current,
          [slot.key]: {
            ...slot,
            offeredVisitTypes: defaultOfferedVisitTypes(slot.devaLocation, form.patientStateClassification, form.paymentClassification),
          },
        };
      }

      const next = { ...current };
      delete next[slot.key];
      return next;
    });
    setSelectedSlotKeys((current) => selectedKeysAfterToggle(current, slot.key, checked));
  };

  const handleDateSelection = (date: ConsultAvailabilityDate, checked: boolean) => {
    date.entries.forEach((entry) => {
      if (entry.selectable && selectedSlotKeys.includes(entry.key) !== checked) {
        const slot = selectedSlotDetails[entry.key] || selectedSlotFromEntry(date, entry);
        if (checked && officeToHomeConflictMessage(slot)) return;
        handleSlotToggle(slot, checked);
      }
    });
  };

  const handleDevaLocationChange = (slotKey: string, value: string) => {
    selectionRevision.current += 1;
    setSelectedSlotDetails((current) => {
      const slot = current[slotKey];
      if (!slot) return current;

      return {
        ...current,
        [slotKey]: {
          ...slot,
          devaLocation: value,
          devaLocationLabel: devaLocationLabel(value),
          offeredVisitTypes: defaultOfferedVisitTypes(value, form.patientStateClassification, form.paymentClassification),
        },
      };
    });
    setCustomizedVisitTypeKeys((current) => {
      const next = new Set(current);
      next.delete(slotKey);
      return next;
    });
  };

  const handleVisitTypeToggle = (slotKey: string, visitType: string, checked: boolean) => {
    selectionRevision.current += 1;
    setSelectedSlotDetails((current) => {
      const slot = current[slotKey];
      if (!slot) return current;

      const offeredVisitTypes = checked
        ? Array.from(new Set([...slot.offeredVisitTypes, visitType]))
        : slot.offeredVisitTypes.filter((value) => value !== visitType);

      return { ...current, [slotKey]: { ...slot, offeredVisitTypes } };
    });
    setCustomizedVisitTypeKeys((current) => new Set(current).add(slotKey));
  };

  const saveCustomSlot = async (draft: CustomSlotDraft) => {
    setSavingCustomSlot(true);
    setCustomSlotError(null);

    try {
      const response = await fetch(props.customSlotUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": props.authenticityToken,
        },
        credentials: "same-origin",
        body: JSON.stringify({
          custom_slot: {
            date: draft.date,
            time: draft.time,
            deva_location: draft.devaLocation,
            offered_visit_types: draft.offeredVisitTypes,
          },
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as CustomSlotResponse;

      if (!response.ok || !payload.slot) {
        throw new Error(responseErrorMessage(payload, "Unable to add that custom consult time."));
      }

      const slot = payload.slot;
      selectionRevision.current += 1;
      setDefaultSelectionActive(false);
      setSelectedSlotDetails((current) => ({ ...current, [slot.key]: slot }));
      setSelectedSlotKeys((current) => selectedKeysAfterToggle(current, slot.key, true));
      setCustomizedVisitTypeKeys((current) => new Set(current).add(slot.key));
      setCustomSlotDefaults(null);
      setFlash({ tone: "success", message: `${slot.scheduleLabel} added to the offered times.` });
    } catch (error) {
      setCustomSlotError(error instanceof Error ? error.message : "Unable to add that custom consult time.");
    } finally {
      setSavingCustomSlot(false);
    }
  };

  const serializedInvite = (): Record<string, unknown> => ({
    recipient_email: form.recipientEmail,
    expires_at: form.expiresAt,
    scheduled_send_at: scheduleForLater ? form.scheduledSendAt : "",
    email_subject: form.emailSubject,
    email_body: form.emailBody,
    patient_state_classification: form.patientStateClassification,
    payment_classification: form.paymentClassification,
    availability_start: availability.startDate,
    slot_type_filter: form.slotTypeFilter,
    selected_slots: selectedSlotKeys.map((sourceKey) => ({
      source_key: sourceKey,
      date: selectedSlotDetails[sourceKey]?.date,
      time: selectedSlotDetails[sourceKey]?.time,
      custom: selectedSlotDetails[sourceKey]?.custom || false,
      deva_location: selectedSlotDetails[sourceKey]?.devaLocation,
      offered_visit_types: selectedSlotDetails[sourceKey]?.offeredVisitTypes || [],
    })),
  });

  const submitInvite = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loadingAvailability || submitting) return;
    setSubmitting(true);
    setErrors([]);
    setFlash(null);

    try {
      const response = await fetch(props.submitUrl, {
        method: props.submitMethod,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": props.authenticityToken,
        },
        credentials: "same-origin",
        body: JSON.stringify({
          consult_scheduling_invite: serializedInvite(),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as WorkspaceResponse;

      if (!response.ok) {
        if (payload.workspace) applyWorkspace(payload.workspace, "submit");
        throw new Error(responseErrorMessage(payload, "Unable to save scheduling link."));
      }

      if (payload.redirectPath) {
        window.location.assign(payload.redirectPath);
        return;
      }

      setFlash({ tone: "success", message: payload.notice || "Scheduling link saved." });
    } catch (error) {
      setFlash({ tone: "error", message: error instanceof Error ? error.message : "Unable to save scheduling link." });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedSlots = selectedSlotsFromKeys(selectedSlotKeys, selectedSlotDetails);
  const patientStateEvidence = props.patientState.evidenceByClassification[form.patientStateClassification] || "No source information is available.";
  const paymentEvidence = props.payment.evidenceByClassification[form.paymentClassification] || "Choose the payment type for this invite.";

  return (
    <form className="space-y-4" onSubmit={submitInvite}>
      <AlertBanner flash={flash} />
      <ErrorList errors={errors} />
      {props.editNotice ? <AlertBanner flash={{ tone: "warning", message: props.editNotice }} /> : null}
      {props.outstandingInviteWarning ? (
        <div className="app-alert-warning">
          <p>{props.outstandingInviteWarning}</p>
          {props.outstandingInviteEditPath ? (
            <p className="mt-2">
              <a
                href={props.outstandingInviteEditPath}
                className="font-semibold underline underline-offset-2 hover:no-underline"
              >
                Edit the existing link instead
              </a>
            </p>
          ) : null}
        </div>
      ) : null}

      {!props.editing ? <section className="app-surface overflow-visible">
        <div className="grid grid-cols-2 gap-3 p-3">
          <ClassificationField
            id="scheduling-invite-patient-state"
            label="Location"
            value={form.patientStateClassification}
            options={props.patientState.options}
            evidenceText={patientStateEvidence}
            linkLabel={props.patientState.linkLabel}
            linkPath={props.patientState.linkPath}
            onChange={handlePatientStateChange}
          />
          <ClassificationField
            id="consult-scheduling-payment-classification"
            label="Payment type"
            value={form.paymentClassification}
            options={props.payment.options}
            evidenceText={paymentEvidence}
            linkLabel={props.payment.linkLabel}
            linkPath={props.payment.linkPath}
            emptyOption="Select"
            onChange={handlePaymentClassificationChange}
          />
        </div>

        <details className="group border-t border-neutral-200/80 dark:border-neutral-800">
          <summary className="flex cursor-pointer list-none items-center gap-3 px-3 py-2.5 marker:hidden [&::-webkit-details-marker]:hidden">
            <span className="text-sm font-semibold text-neutral-950 dark:text-white">Invite details</span>
            <span className="min-w-0 flex-1 truncate text-sm text-neutral-500 dark:text-neutral-400">
              {form.recipientEmail || "No recipient email"}
            </span>
            <span className="text-neutral-400 dark:text-neutral-500"><ChevronIcon /></span>
          </summary>
          <div className="border-t border-neutral-200/80 p-3 dark:border-neutral-800">
            <p className="mb-3 text-sm text-neutral-500 dark:text-neutral-400">
              The email body can include <code>{props.linkPlaceholder}</code>. If omitted, the scheduling link is appended automatically.
            </p>
            <EmailDetailsSection
              form={form}
              linkPlaceholder={props.linkPlaceholder}
              onChange={updateForm}
              showHeading={false}
            />
          </div>
        </details>
      </section> : null}

      <ExistingAppointmentsNotice appointments={props.existingAppointments} appointmentType="consult" />

      <section className={`app-surface p-4 ${loadingAvailability ? "opacity-70" : ""}`} aria-busy={loadingAvailability}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-neutral-950 dark:text-white">Availability</h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">30 min · Arizona time</p>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2" aria-label="Availability controls">
          <div className="flex min-w-0 items-center gap-1">
            <button type="button" className="flex h-9 w-8 shrink-0 items-center justify-center rounded text-xl hover:bg-neutral-100 disabled:opacity-40 pointer-coarse:min-h-11 pointer-coarse:min-w-11 dark:hover:bg-neutral-800" disabled={!availability.previousStartDate || loadingAvailability}
              aria-label="Previous" onClick={() => availability.previousStartDate && pageAvailability(availability.previousStartDate)}>‹</button>
            <details ref={datePickerRef} className="group relative min-w-0"
              onKeyDown={(event) => { if (event.key === "Escape") { event.currentTarget.open = false; event.currentTarget.querySelector("summary")?.focus(); } }}>
              <summary className="flex cursor-pointer list-none items-center gap-1 rounded px-1 py-2 text-sm font-medium marker:hidden hover:bg-neutral-100 pointer-coarse:min-h-11 dark:hover:bg-neutral-800 [&::-webkit-details-marker]:hidden">
                <span aria-live="polite">{availability.rangeLabel}</span><ChevronIcon />
              </summary>
              <div className="absolute left-0 top-full z-30 mt-1 w-64 max-w-[calc(100vw-6rem)] rounded-lg border border-neutral-200 bg-white p-3 shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
                <label className="text-xs font-medium text-neutral-600 dark:text-neutral-300">Start from
                  <input type="date" className="app-input" value={jumpDate} min={availability.today} disabled={loadingAvailability}
                    onChange={(event) => setJumpDate(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      if (jumpDate && event.currentTarget.validity.valid) pageAvailability(jumpDate);
                    }} />
                </label>
                <button type="button" className="app-button-secondary mt-2" disabled={!jumpDate || jumpDate < availability.today || loadingAvailability}
                  onClick={() => pageAvailability(jumpDate)}>Apply date</button>
              </div>
            </details>
            <button type="button" className="flex h-9 w-8 shrink-0 items-center justify-center rounded text-xl hover:bg-neutral-100 disabled:opacity-40 pointer-coarse:min-h-11 pointer-coarse:min-w-11 dark:hover:bg-neutral-800" disabled={loadingAvailability}
              aria-label="Next" onClick={() => pageAvailability(availability.nextStartDate)}>›</button>
          </div>
          <details ref={availabilityOptionsRef} className="group relative ml-auto"
            onKeyDown={(event) => { if (event.key === "Escape") { event.currentTarget.open = false; event.currentTarget.querySelector("summary")?.focus(); } }}>
            <summary className="app-button-secondary cursor-pointer list-none marker:hidden [&::-webkit-details-marker]:hidden">Options <ChevronIcon /></summary>
            <div className="absolute right-0 top-full z-30 mt-1 w-64 rounded-lg border border-neutral-200 bg-white p-3 shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
              <label className="text-xs font-medium text-neutral-600 dark:text-neutral-300">Deva location filter
                <select className="app-select" value={form.slotTypeFilter} onChange={(event) => handleSlotTypeFilterChange(event.target.value)}>
                  {SLOT_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <button type="button" className="app-button-secondary mt-3" disabled={loadingAvailability}
                onClick={() => openCustomSlot(availability.startDate, "10:00")}>Add custom time</button>
            </div>
          </details>
        </div>
        {form.slotTypeFilter !== "all" ? <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">Available times: {devaLocationLabel(form.slotTypeFilter)}</p> : null}

        {availability.view === "calendar" ? (
          <ConsultAvailabilityCalendar
            dates={availability.dates}
            selectedSlots={selectedSlots}
            disabled={loadingAvailability}
            emptyMessage={availability.emptyMessage}
            onDateSelection={handleDateSelection}
            onSlotToggle={handleSlotToggle}
            onCustomSlot={openCustomSlot}
          />
        ) : (
          <ClassicConsultAvailability
            visibleDates={availability.dates}
            selectedSlotKeys={selectedSlotKeys}
            selectedSlots={selectedSlots}
            loadingAvailability={loadingAvailability}
            showBookedAppointments={true}
            emptyMessage={availability.emptyMessage}
            handleDateSelection={handleDateSelection}
            handleSlotToggle={handleSlotToggle}
          />
        )}

        <SelectedConsultSlots
          slots={selectedSlots}
          visibleDates={availability.dates.map((date) => date.date)}
          locationOptions={DEVA_LOCATION_OPTIONS}
          visitTypeOptions={VISIT_TYPE_OPTIONS}
          onRemove={(slot) => handleSlotToggle(slot, false)}
          onLocationChange={handleDevaLocationChange}
          onVisitTypeToggle={handleVisitTypeToggle}
        />
      </section>

      <EmailDeliverySection
        editing={props.editing}
        scheduledSendAt={form.scheduledSendAt}
        scheduleForLater={scheduleForLater}
        onScheduleForLaterChange={setScheduleForLater}
        onChange={(value) => updateForm("scheduledSendAt", value)}
        firstOfferedAppointment="first offered consult"
      />

      <div className="flex flex-wrap gap-2">
        <button type="submit" className="app-button-primary" disabled={submitting || loadingAvailability}>
          {submitting ? "Saving..." : !props.editing && scheduleForLater ? "Schedule email" : props.submitLabel}
        </button>
        <button
          type="button"
          className="app-button-secondary"
          disabled={submitting || loadingAvailability}
          onClick={() => openSchedulingPreview(props.previewUrl, props.authenticityToken, serializedInvite())}
        >
          Preview patient experience
        </button>
        <a href={props.cancelPath} className="app-button-secondary">
          Cancel
        </a>
      </div>

      {customSlotDefaults ? (
        <CustomSlotModal
          defaultDate={customSlotDefaults.date}
          defaultTime={customSlotDefaults.time}
          defaultVisitTypes={defaultOfferedVisitTypes("office", form.patientStateClassification, form.paymentClassification)}
          saving={savingCustomSlot}
          error={customSlotError}
          onClose={() => setCustomSlotDefaults(null)}
          onSave={saveCustomSlot}
        />
      ) : null}
    </form>
  );
}
